import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const STATE = { running: 'En curso', succeeded: 'Verificada', rejected: 'No ejecutada / rechazada', needs_review: 'Requiere revisión' };
const ACTION = { create_appointment: 'Crear cita', cancel_appointment: 'Cancelar cita', reschedule_appointment: 'Reprogramar cita', handoff: 'Solicitar ayuda', configuration_apply: 'Aplicar configuración', configuration_undo: 'Deshacer configuración' };

export default function AgentOperationsPanel({ clinicId, token, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const base = `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/agent-executions`;
      const config = { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` },
        params: clinicId ? { clinic_id: clinicId } : {}, signal };
      const [health, operations] = await Promise.all([axios.get(`${base}/health`, config), axios.get(base, config)]);
      if (!signal?.aborted) setData({ ...health.data, ...operations.data });
    } catch (e) {
      if (!signal?.aborted) { setData(null); setError('No se pudo consultar el registro de operaciones.'); }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [clinicId, token]);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);
  return <section className="bo-card bo-operations">
    <div className="bo-section-head">
      <h2>Control de operaciones de IA</h2>
      <button onClick={() => load()} disabled={loading}>{loading ? 'Consultando…' : 'Actualizar operaciones'}</button>
    </div>
    <div className="space-y-4">
      {error && <p role="alert" className="bo-error">{error}</p>}
      {!data && !error && <p role="status">Consultando operaciones…</p>}
      {data && <>
        <div className="bo-metrics compact">
          {Object.entries(STATE).map(([key, label]) => <div key={key}>
            <span>{label}</span><strong>{data.counts[key] || 0}</strong>
          </div>)}
        </div>
        {(data.stale_running > 0 || data.locks.length > 0) && <p className="bo-notice">
          {data.stale_running} operaciones llevan más de 10 minutos sin actualizarse. {data.locks.length} bloqueos activos.
          {' '}Comprueba el resultado guardado antes de repetir una operación pendiente.
        </p>}
        <div className="bo-table-wrap">
          <table>
            <caption className="sr-only">Operaciones de IA y evidencia de ejecución</caption>
            <thead><tr>{['Acción', 'Estado', 'Fecha', 'Referencia'].map(x => <th key={x} scope="col" className="p-2">{x}</th>)}</tr></thead>
            <tbody>{data.operations.map(row => <tr key={row.id} className="border-t">
              <td className="p-2">{ACTION[row.action] || row.action}</td>
              <td className="p-2"><span className={`bo-status ${row.state === 'succeeded' ? 'good' : row.state === 'needs_review' ? 'bad' : ''}`}>{STATE[row.state] || row.state}</span></td>
              <td className="p-2 whitespace-nowrap">{new Date(row.created_at).toLocaleString('es-MX', {timeZone: 'America/Mexico_City'})}</td>
              <td className="p-2"><details><summary className="cursor-pointer">Ver referencia</summary>
                <p className="max-w-xs break-all">Operación: {row.id}</p>
                <p className="max-w-xs break-all">Clínica: {row.clinic_id}</p>
                {(row.verification?.appointment_id || row.evidence?.appointment_id) && <p className="max-w-xs break-all">Cita: {row.verification?.appointment_id || row.evidence?.appointment_id}</p>}
                {(row.verification?.proposal_id || row.evidence?.proposal_id) && <p className="max-w-xs break-all">Propuesta: {row.verification?.proposal_id || row.evidence?.proposal_id}</p>}
              </details></td>
            </tr>)}</tbody>
          </table>
          {data.operations.length === 0 && <p className="bo-empty">Todavía no hay operaciones registradas con este ejecutor.</p>}
        </div>
        <p><small>{data.scope} Actualizado: {new Date(data.checked_at).toLocaleString('es-MX', {timeZone: 'America/Mexico_City'})}.</small></p>
      </>}
    </div>
  </section>;
}
